/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `FileUploadArea` for the Caintra Connect frontend.
 * @module modals/ProposalCreateModal/FileUploadArea
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React from "react";
import styles from "./FileUploadArea.module.css";
import { ReactComponent as UploadIcon } from "../../assets/icons/ProposalCreateModal/UploadIcon.svg";
import { ReactComponent as UploadedIcon } from "../../assets/icons/ProposalCreateModal/UploadedIcon.svg";
import { CircularProgress } from "@mui/material";
import { formatFileSize } from "../../logic/proposal/index.js";
import {
  decodeFileName,
  handleFileUploadAreaDownload,
} from "../../utils/common";
import { useTranslation } from "react-i18next";

const FileUploadArea = ({
  file,
  isUploading,
  isDragging,
  error,
  onFileChange,
  onDragOver,
  onDragLeave,
  onDrop,
  onClick,
  reduceHeightForTablet = false,
  reduceHeightForMobile = false,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const canDownload = Boolean(file?.url || file instanceof Blob);

  const handleDownloadClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!file) {
      return;
    }

    if (file.url) {
      await handleFileUploadAreaDownload(file.url, file.name);
      return;
    }

    if (file instanceof Blob) {
      const downloadUrl = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", file.name || "document");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    }
  };

  const handleReplaceClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClick?.(e);
  };

  const handleAreaClick = (e) => {
    if (disabled) {
      if (file?.url) {
        handleDownloadClick(e);
      }
      return;
    }

    // When a file is already present, clicking the area downloads it.
    // Replacement is done via the blue link or drag and drop.
    if (file && canDownload) {
      handleDownloadClick(e);
      return;
    }

    onClick?.(e);
  };

  const handleAreaDragOver = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    onDragOver?.(e);
  };

  const handleAreaDragLeave = (e) => {
    if (disabled) {
      return;
    }
    onDragLeave?.(e);
  };

  const handleAreaDrop = (e) => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onDrop?.(e);
  };

  const renderEditableFilePrompt = () => {
    if (file) {
      if (isDragging) {
        return (
          <p className={styles.firstUploadText}>
            {t("components.fileUploadArea.uploadText1")}
          </p>
        );
      }

      return (
        <p className={styles.secondUploadText}>
          {t("components.fileUploadArea.replaceFileText1")}
          <span className={styles.browseLink} onClick={handleReplaceClick}>
            {t("components.fileUploadArea.replaceFileText2")}
          </span>
        </p>
      );
    }

    return (
      <>
        <p className={styles.firstUploadText}>
          {isDragging
            ? t("components.fileUploadArea.uploadText2")
            : t("components.fileUploadArea.uploadText3")}
        </p>
        {!isDragging && (
          <p className={styles.secondUploadText}>
            {t("components.fileUploadArea.uploadText4")}
            <span className={styles.browseLink}>
              {t("components.fileUploadArea.uploadText5")}
            </span>
          </p>
        )}
      </>
    );
  };

  return (
    <div
      className={`${styles.fileUploadArea} ${
        isDragging && !disabled ? styles.dragOver : ""
      } ${error ? styles.error : ""} ${
        reduceHeightForTablet ? styles.reduceHeightForTablet : ""
      } ${reduceHeightForMobile ? styles.reduceHeightForMobile : ""} ${
        disabled ? styles.disabled : ""
      } ${disabled && file?.url ? styles.downloadable : ""} ${
        file ? styles.hasFile : ""
      }`}
      onDragOver={handleAreaDragOver}
      onDragLeave={handleAreaDragLeave}
      onDrop={handleAreaDrop}
      onClick={handleAreaClick}
      role={disabled && file?.url ? "button" : undefined}
      aria-disabled={disabled && !file?.url ? true : undefined}
    >
      {isUploading ? (
        <>
          <CircularProgress size={48} className={styles.loadingSpinner} />
          <p className={styles.firstUploadText}>
            {t("components.fileUploadArea.uploadingFile")}
          </p>
        </>
      ) : disabled ? (
        <>
          {file ? (
            <>
              <UploadedIcon className={styles.uploadedIcon} />
              <div className={styles.uploadedFileName}>
                {decodeFileName(file.name)}
                <span className={styles.fileSize}>
                  {file.size !== 0 ? `(${formatFileSize(file.size)})` : ""}
                </span>
              </div>
              {file.url && (
                <p className={styles.firstUploadText}>
                  {t("components.fileUploadArea.downloadText")}
                </p>
              )}
            </>
          ) : (
            <>
              <UploadIcon className={styles.uploadIcon} />
              <p className={styles.firstUploadText}>
                {t("components.fileUploadArea.noFileText")}
              </p>
            </>
          )}
          {error && <p className={styles.errorMessage}>{error}</p>}
        </>
      ) : (
        <>
          {file ? (
            <>
              <UploadedIcon className={styles.uploadedIcon} />
              <div className={styles.uploadedFileName}>
                {decodeFileName(file.name)}
                <span className={styles.fileSize}>
                  {file.size !== 0 ? `(${formatFileSize(file.size)})` : ""}
                </span>
              </div>
            </>
          ) : (
            <UploadIcon className={styles.uploadIcon} />
          )}
          {error && <p className={styles.errorMessage}>{error}</p>}
          {renderEditableFilePrompt()}
        </>
      )}
    </div>
  );
};

export default FileUploadArea;
